import i18next, { t } from 'i18next'
import { FC, useCallback, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'

import { Download } from '@mui/icons-material'
import { Box, Button, Dialog, DialogContent, DialogProps, Divider, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'

import { SSEStreamStatus, useGetLiveEventAISummaryStreamQuery } from '../../../../../api/live-event-predictions'
import gameRefineryLogo from '../../../../../assets/images/gamerefinery-logo.png'
import AIAvatar from '../../../../../components/AIAvatar/AIAvatar'
import GRDialogTitle, { GRDialogCloseButton } from '../../../../../components/GRDialogTitle/GRDialogTitle'
import { GRMarkdown } from '../../../../../components/GRMarkdown/GRMarkdown'
import { ImageCarouselModal } from '../../../../../components/ImageCarouselModal/ImageCarouselModal'
import { useAppDispatch } from '../../../../../hooks/storeHooks'
import { useExportPdf } from '../../../../../hooks/useExportPdf'
import { useGRTypewriter } from '../../../../../hooks/useGRTypewriter'
import { cuteRoboticThoughts } from '../../../../../pages/AIAnalyst/AIAnalystPageInfo'
import { GameIcon } from '../../../../game'
import { displaySnackBar } from '../../../../snackbar'
import { LiveEventGameAIPrompt } from '../../../types/LiveEventGameAIPrompt'
import { TrackedGame } from '../../../types/TrackedGame'

type LiveEventGameAIButtonDialogProps = DialogProps & {
  data: LiveEventGameAIButtonDialogData
}

export type LiveEventGameAIButtonDialogData = {
  selectedTrackedGame: TrackedGame
  selectedPrompt: LiveEventGameAIPrompt
  trackedGames: TrackedGame[]
  liveEventGameAIPrompts: LiveEventGameAIPrompt[]
}

export const LiveEventGameAIButtonDialog: FC<LiveEventGameAIButtonDialogProps> = ({
  open,
  onClose,
  data: { selectedTrackedGame, selectedPrompt, trackedGames, liveEventGameAIPrompts },
}) => {
  const dispatch = useAppDispatch()
  const [selectedGameId, setSelectedGameId] = useState<string>(selectedTrackedGame.game.id)
  const [selectedLiveEventAIPrompt, setSelectedLiveEventAIPrompt] = useState<string>(selectedPrompt)
  const [imageCarouselModalOpen, setImageCarouselModalOpen] = useState(false)
  const pdfContentRef = useRef<HTMLDivElement>(null)
  const pdfDateRef = useRef<HTMLElement>(null)
  const { exportPdf, isExporting } = useExportPdf(pdfContentRef)

  const { data: streamResponse } = useGetLiveEventAISummaryStreamQuery(
    { gameId: selectedGameId, prompt: selectedLiveEventAIPrompt as LiveEventGameAIPrompt },
    { skip: !selectedGameId || !selectedLiveEventAIPrompt || !open }
  )

  const isFetchingAIResponse = useMemo(() => {
    return streamResponse && streamResponse.status === SSEStreamStatus.FETCHING
  }, [streamResponse])

  const isStreamingAIResponse = useMemo(() => {
    return streamResponse && streamResponse.status === SSEStreamStatus.STREAMING
  }, [streamResponse])

  const isClosedAIResponse = useMemo(() => {
    return streamResponse && streamResponse.status === SSEStreamStatus.CLOSED
  }, [streamResponse])

  const handleGameIdChange = (event: SelectChangeEvent) => {
    const value = event.target.value as string
    setSelectedLiveEventAIPrompt('')
    setSelectedGameId(value)
  }

  const handleLiveEventAIPromptChange = (event: SelectChangeEvent) => {
    const value = event.target.value as string
    setSelectedLiveEventAIPrompt(value)
  }

  const menuItems = useMemo(() => {
    return liveEventGameAIPrompts.map((key) => {
      if (selectedTrackedGame.aiSummariesAvailable?.includes(key)) {
        return (
          <MenuItem key={key} value={key}>
            <Trans i18nKey={`live-events:ai_button_${key}`} />
          </MenuItem>
        )
      }

      return null
    })
  }, [selectedTrackedGame, liveEventGameAIPrompts])

  const aiLoadingText = useMemo(() => {
    return cuteRoboticThoughts[Math.floor(Math.random() * cuteRoboticThoughts.length)]
  }, [])

  const filteredTrackedGames = useMemo(() => {
    return trackedGames.filter((game) => game.aiSummariesAvailable && game.aiSummariesAvailable.length > 0)
  }, [trackedGames])

  const {
    text: typewritedAIResponse,
    isFinished: isFinishedTypingAIResponse,
    isTyping: isTypingAIResponse,
    isDelaying: isDelayingAIResponse,
  } = useGRTypewriter(isClosedAIResponse || selectedLiveEventAIPrompt ? streamResponse?.data || '' : '', {
    initialDelayMs: 5000,
    minDelayMs: 3,
    maxDelayMs: 10,
    minCharsPerTick: 2,
    maxCharsPerTick: 8,
    punctuationPauseMs: 50,
    newlinePauseMs: 100,
  })

  const selectedGameName = useMemo(() => {
    return trackedGames.find((g) => g.game.id === selectedGameId)?.game.resolvedName || ''
  }, [trackedGames, selectedGameId])

  const handleExportPdf = useCallback(() => {
    if (pdfDateRef.current) {
      pdfDateRef.current.textContent = new Intl.DateTimeFormat(i18next.language, { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date())
    }
    const safeGameName = selectedGameName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
    exportPdf(`gamerefinery-ai-summary-${safeGameName || 'unknown'}.pdf`).catch((error) => {
      dispatch(displaySnackBar({ message: t('common:general_error_message'), severity: 'error', open: true }))
      // eslint-disable-next-line no-console
      console.error('Failed to export PDF', error)
    })
  }, [dispatch, exportPdf, selectedGameName])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
      className="LiveEventGameAIButtonDialog"
      PaperProps={{ sx: { minHeight: '90vh', maxHeight: '90vh' } }}
    >
      <GRDialogTitle>
        <Grid container alignItems="center">
          <Grid item xs={3}>
            {' '}
          </Grid>
          <Grid item xs={6} container direction="column" alignItems="center">
            <Trans i18nKey="live-events:ai_summary_dialog_title" />{' '}
            {selectedLiveEventAIPrompt && (
              <>
                {' '}
                / <Trans i18nKey={`live-events:ai_button_${selectedLiveEventAIPrompt}`} />
              </>
            )}
          </Grid>
          <Grid item xs={3} container direction="column" alignItems="flex-end">
            <GRDialogCloseButton onClick={onClose} share={false} />
          </Grid>
        </Grid>
      </GRDialogTitle>

      <Divider />

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <FormControl size="small" variant="outlined" disabled={isFetchingAIResponse} fullWidth>
              <InputLabel htmlFor="game-id-selector-label">{t('compare:select_game')}</InputLabel>
              <Select
                labelId="game-id-selector-label"
                size="small"
                value={selectedGameId}
                onChange={handleGameIdChange}
                label={t('compare:select_game')}
                disabled={!isFinishedTypingAIResponse}
              >
                {filteredTrackedGames.map((trackedGame) => {
                  return (
                    <MenuItem key={trackedGame.game.id} value={trackedGame.game.id}>
                      <Grid container spacing={1} wrap="nowrap" sx={{ overflow: 'hidden' }}>
                        <Grid item sx={{ flexShrink: 0 }}>
                          <div style={{ display: 'block', height: '16px' }}>
                            <GameIcon
                              src={trackedGame.game.icons || trackedGame.game.icon}
                              gameName={trackedGame.game.resolvedName}
                              size="xtiny"
                              discontinued={trackedGame.discontinued}
                            />
                          </div>
                        </Grid>
                        <Grid item sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, maxWidth: '85%' }}>
                          {trackedGame.game.name}
                        </Grid>
                      </Grid>
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={8}>
            <FormControl size="small" variant="outlined" disabled={isFetchingAIResponse} fullWidth>
              <InputLabel htmlFor="ai-prompt-selector-label">{t('common:select_ai_response_type')}</InputLabel>
              <Select
                labelId="ai-prompt-selector-label"
                size="small"
                value={selectedLiveEventAIPrompt}
                onChange={handleLiveEventAIPromptChange}
                label={t('common:select_ai_response_type')}
                disabled={!isFinishedTypingAIResponse}
              >
                {menuItems}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {selectedLiveEventAIPrompt && (
          <>
            <Box my={2}>
              <Divider />
            </Box>

            <Box sx={{ padding: '5px 20px', backgroundColor: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '20px' }}>
              {(isDelayingAIResponse || isFetchingAIResponse || isStreamingAIResponse) && (
                <>
                  <Box my={6} textAlign={'center'}>
                    <AIAvatar className="AnimateAvatar" />
                    <Box mt={1} color="#555">
                      {aiLoadingText}
                    </Box>
                  </Box>

                  {isTypingAIResponse && (
                    <Box my={2}>
                      <Divider />
                    </Box>
                  )}
                </>
              )}

              {isClosedAIResponse && streamResponse && streamResponse.data && (
                <div ref={pdfContentRef}>
                  <Box mb={2} className="pdf-export-only" sx={{ display: 'none' }}>
                    <Grid container alignItems="center" justifyContent="space-between">
                      <Grid item>
                        <img src={gameRefineryLogo} alt="GameRefinery" height={32} />
                      </Grid>
                      <Grid item textAlign="right">
                        <Box sx={{ fontWeight: 600, fontSize: '14px' }}>{t('live-events:ai_summary_dialog_title')}</Box>
                        <Box ref={pdfDateRef} sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)', whiteSpace: 'nowrap' }} />
                      </Grid>
                    </Grid>
                    <Box mt={2} mb={1} textAlign="center">
                      <h1 style={{ margin: 0 }}>{selectedGameName}</h1>
                    </Box>
                    <Divider />
                  </Box>

                  <GRMarkdown text={typewritedAIResponse} isTyping={isTypingAIResponse} />

                  {isFinishedTypingAIResponse && streamResponse.imageUrl && (
                    <>
                      <Box my={2}>
                        <Divider />
                      </Box>

                      <Box mt={2} mb={1}>
                        <img
                          className="ImageRoundedBorderWithShadow"
                          alt="Summary"
                          src={streamResponse.imageUrl}
                          width="100%"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setImageCarouselModalOpen(true)}
                        />
                      </Box>

                      <ImageCarouselModal
                        imageUrls={[streamResponse.imageUrl]}
                        open={imageCarouselModalOpen}
                        onClose={() => setImageCarouselModalOpen(false)}
                        initialIndex={0}
                      />
                    </>
                  )}

                  {isFinishedTypingAIResponse && (
                    <Box my={2} display="flex" justifyContent="center" className="pdf-export-hide">
                      <Button variant="contained" startIcon={<Download />} onClick={handleExportPdf} disabled={isExporting}>
                        {t('common:download_pdf')}
                      </Button>
                    </Box>
                  )}
                </div>
              )}
            </Box>

            {!isFinishedTypingAIResponse && <Box my={20} />}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
